import {
  TableData,
  TableHeaderCategoryConfig,
} from "../../../../../components/tables/tableContext/TableContext";
import { TableSectionData } from "../../../../../components/tables/sectionTable/sectionTable.interface";
import { getRecomandedCell } from "../../../../../helpers/qrHelper";
import {
  RecallAwbInterface,
  RecallEntryDetailsDataInterface,
  RecallRoutingClientInterface,
  RecallRoutingExceptionInterface,
  RecallSaleListInterface,
  RecallSupplierReceiptListInterface,
  RecallWarehouseStockListInterface,
} from "../../../../../redux/interfaces/recall.interface";
import { dateToString, getDateAndTime } from "../../../../../utils/dateAndTime";

export const recallReceivedRowClassName =
  "bg-success-subtle hover:!bg-success-subtle [&>td:first-child]:border-l-4 [&>td:first-child]:border-success";

export const routingExceptionLabels: Record<
  RecallRoutingExceptionInterface["reason"],
  string
> = {
  multiple_routes: "Mai multe rute",
  missing_route: "Ruta lipsa",
  unmapped_node: "Nod neconfigurat",
};

export const recallClientsTableHeader: TableHeaderCategoryConfig[] = [
  { value: "pharmacyName", label: "Farmacie", minW: 200 },
  { value: "buyerName", label: "Cumparator", minW: 180 },
  { value: "barcodeToken", label: "Document", minW: 220 },
  { value: "awb", label: "AWB", minW: 150 },
  { value: "positionCount", label: "Vanzari", minW: 200 },
];

export const getRecallAwbTableData = (
  awb?: RecallAwbInterface | null,
): TableData["data"][number] => ({
  title: `Plic: ${awb?.awbNumbers.length ? awb.awbNumbers.join(", ") : "-"}`,
  subtitle: `Retur: ${
    awb?.returnAwb?.awbNumbers.length
      ? awb.returnAwb.awbNumbers.join(", ")
      : "-"
  }`,
  subtitleClassName: "mt-1",
});

const createRecallClientRowMapper = (
  entry: RecallEntryDetailsDataInterface,
) => {
  const notices = new Map(
    entry.notifications.map((item) => [item.clientPlId, item]),
  );
  const documents = new Map(
    entry.documents.map((item) => [item.clientPlId, item]),
  );
  const awbs = new Map(entry.awbs.map((item) => [item.clientPlId, item]));
  const emails = new Map(entry.emails.map((item) => [item.clientPlId, item]));
  const salesByClient = new Map<number, RecallSaleListInterface[]>();
  entry.sales.data.forEach((sale) => {
    const sales = salesByClient.get(sale.clientPlId) ?? [];
    sales.push(sale);
    salesByClient.set(sale.clientPlId, sales);
  });

  return (client: RecallRoutingClientInterface): TableData => {
    const { clientPlId } = client;
    const notice = notices.get(clientPlId);
    const document = documents.get(clientPlId);
    const awb = awbs.get(clientPlId);
    const email = emails.get(clientPlId);
    const sales = salesByClient.get(clientPlId) ?? [];
    const soldQuantity = sales.reduce(
      (total, sale) => total + Number(sale.soldQuantity),
      0,
    );
    const pharmacyName = client.pharmacyName ?? `Farmacie ${clientPlId}`;

    return {
      id: clientPlId,
      rowClassName:
        notice?.status === "received" ? recallReceivedRowClassName : undefined,
      crudData: {
        idCampaign: entry.idCampaign,
        clientPlId,
        pharmacyName,
        pharmacyEmail: client.email,
        sales,
        notice,
        document,
        awb,
        email,
      },
      data: [
        { title: client.pharmacyName ?? "-" },
        { title: client.buyerName ?? "-" },
        {
          title: `Generat: ${getDateAndTime(document?.generatedAt, "-")}`,
          subtitle: `Primit: ${getDateAndTime(notice?.receivedAt, "-")}`,
          subtitleClassName: "mt-1",
        },
        getRecallAwbTableData(awb),
        {
          title: `Vanzari: ${sales.length}`,
          subtitle: `Cantitate: ${Number(soldQuantity.toFixed(6))}`,
          subtitleClassName: "mt-1",
        },
      ],
    };
  };
};

export const getRecallClientsTableData = (
  entry: RecallEntryDetailsDataInterface,
): TableData[] => {
  const getClientRow = createRecallClientRowMapper(entry);

  return [
    ...entry.routingGroups.flatMap((node) =>
      node.routes.flatMap((route) => route.clients),
    ),
    ...entry.routingExceptions,
  ].map(getClientRow);
};

export const getRecallClientsTableSections = (
  entry: RecallEntryDetailsDataInterface,
): TableSectionData[] => {
  const getClientRow = createRecallClientRowMapper(entry);

  const sections: TableSectionData[] = entry.routingGroups.map((node) => ({
    id: `node-${node.nodeId}`,
    title: `Nod ATS: ${node.nodeName}`,
    crudData: { ...node, type: "node" },
    sections: node.routes.map((route) => ({
      id: `route-${route.routeId}`,
      title: `Ruta: ${route.routeName ?? route.routeId}`,
      crudData: { ...route, nodeId: node.nodeId, type: "route" },
      data: route.clients.map(getClientRow),
    })),
  }));

  const exceptionSections = new Map<
    RecallRoutingExceptionInterface["reason"],
    TableSectionData
  >();
  entry.routingExceptions.forEach((client) => {
    const { reason } = client;
    let section = exceptionSections.get(reason);
    if (!section) {
      section = {
        id: reason,
        title: routingExceptionLabels[reason],
        crudData: { type: "routing-exception", reason },
        data: [],
      };
      exceptionSections.set(reason, section);
    }
    section.data?.push(getClientRow(client));
  });

  if (exceptionSections.size) {
    sections.push({
      id: "routing-exceptions",
      title: "Farmacii fara grupare",
      crudData: { type: "routing-exceptions" },
      sections: Array.from(exceptionSections.values()),
    });
  }

  return sections;
};

export const recallStockTableHeader: TableHeaderCategoryConfig[] = [
  { value: "productCode", label: "Cod produs", minW: 150 },
  { value: "lot", label: "Lot", minW: 150 },
  { value: "bbd", label: "Data expirare (BBD)", minW: 150 },
  { value: "totalQuantity", label: "Stoc actual", minW: 150 },
];

export const getRecallStockTableData = (
  data: RecallWarehouseStockListInterface[],
): TableData[] =>
  data.map((item) => ({
    id: item.idRecallLine,
    crudData: item,
    data: [
      { title: item.productCode },
      { title: item.lot },
      { title: dateToString(item.bbd) },
      { title: String(item.totalQuantity) },
    ],
    extraDataPreset: "grouped",
    extraData: item.locations.map((location) => [
      {
        title: `Locatie: ${
          getRecomandedCell({
            alias: location.locationAlias,
            cladire: location.building,
            rand: location.row,
            coloana: location.column,
            polita: location.shelf,
            celula: location.cell,
            alee: location.aisle,
            insula: location.island,
          }) || "Indisponibila"
        }`,
        config: { colSpan: 3 },
      },
      { title: String(location.quantity) },
    ]),
  }));

export const recallSalesTableHeader: TableHeaderCategoryConfig[] = [
  { value: "productCode", label: "Cod produs", minW: 130 },
  { value: "lot", label: "Lot", minW: 130 },
  { value: "bbd", label: "Data expirare (BBD)", minW: 150 },
  { value: "orderNo", label: "Numar comanda", minW: 130 },
  { value: "invoiceNumber", label: "Factura", minW: 150 },
  { value: "invoiceDate", label: "Data facturii", minW: 130 },
  { value: "soldQuantity", label: "Cantitate vanduta", minW: 140 },
  { value: "soldUnit", label: "UM", minW: 75 },
];

export const getRecallSalesTableData = (
  data: RecallSaleListInterface[],
): TableData[] =>
  data.map((item) => ({
    id: item.idRecallSale,
    crudData: item,
    data: [
      { title: item.productCode },
      { title: item.lot },
      { title: dateToString(item.bbd) },
      { title: String(item.orderNo) },
      { title: item.invoiceNumber },
      { title: dateToString(item.invoiceDate) },
      { title: item.soldQuantity },
      { title: item.soldUnit ?? "-" },
    ],
  }));

export const recallReceiptsTableHeader: TableHeaderCategoryConfig[] = [
  { value: "productCode", label: "Cod produs", minW: 130 },
  { value: "lot", label: "Lot", minW: 130 },
  { value: "bbd", label: "Data expirare (BBD)", minW: 150 },
  { value: "supplierName", label: "Furnizor", minW: 200 },
  { value: "receivedQuantity", label: "Cantitate receptionata", minW: 150 },
  { value: "lastReceiptAt", label: "Data receptiei", minW: 170 },
];

export const getRecallReceiptsTableData = (
  data: RecallSupplierReceiptListInterface[],
): TableData[] =>
  data.map((item) => ({
    id: `${item.idRecallLine}-${item.supplierId}-${item.receptionFlowId}`,
    crudData: item,
    data: [
      { title: item.productCode },
      { title: item.lot },
      { title: dateToString(item.bbd) },
      { title: item.supplierName ?? String(item.supplierId) },
      { title: item.receivedQuantity },
      {
        title: getDateAndTime(item.lastReceiptAt),
        subtitle: `Prima receptie: ${getDateAndTime(item.firstReceiptAt)}`,
      },
    ],
  }));

export const recallReceiptInvoicesTableHeader: TableHeaderCategoryConfig[] = [
  { value: "invoiceNumber", label: "Factura", minW: 180 },
  { value: "invoicedQuantity", label: "Cantitate facturata", minW: 170 },
  { value: "invoiceDate", label: "Data facturii", minW: 150 },
];

export const getRecallReceiptInvoicesTableData = (
  data: RecallSupplierReceiptListInterface["invoiceDetails"],
): TableData[] =>
  data.map((item, index) => ({
    id: index,
    crudData: item,
    data: [
      { title: item.invoiceNumber },
      { title: String(item.invoicedQuantity) },
      { title: dateToString(item.invoiceDate, "-") },
    ],
  }));
